import React, { useMemo } from "react";
import { AlertComparizonType, AlertNotification, AlertValueType, PatientSettings } from "@shared/models";
import { Icon, Tooltip } from "@shared/components";
import { convertDateToTimezone, getFormattedDateTime } from "@shared/utils";
import { useGetDifferenceAlertTextShort } from "@containers/Alert/hooks";
import { TIMEZONE } from "@shared/constants";
import { IconType } from "@shared/components/Icon/Icon";

import "./index.scss";

interface AlertNotificationsRowComponentProps {
  item: AlertNotification;
  patientSettings?: PatientSettings;
  onClickMarkAsRead?: (alertNotification: AlertNotification) => void;
}

const AlertNotificationsRowComponent = (props: AlertNotificationsRowComponentProps) => {
  const { item, patientSettings, onClickMarkAsRead } = props;
  const differenceText = useGetDifferenceAlertTextShort(item, false);

  const notificationValue = useMemo(() => {
    return Number(item.value).toFixed(0);
  }, [item.value]);

  const notificationSignalType = useMemo(() => {
    return String(item.signal_type).toUpperCase();
  }, [item.signal_type]);

  const percentageColor = useMemo(() => {
    return item.comparison_type === AlertComparizonType.ABOVE ? "green" : "red";
  }, [item.comparison_type]);

  const percentageIcon = useMemo(() => {
    return item.comparison_type === AlertComparizonType.ABOVE ? "percentage-up" : "percentage-down";
  }, [item.comparison_type]);

  const patientTimeZone = useMemo(() => {
    if (!patientSettings) return undefined;

    return patientSettings?.timezone || TIMEZONE.UTC;
  }, [patientSettings]);

  const tooltipCommentLabal = useMemo(() => {
    if (!item.comment) return null;

    const MAX_COMMENT_LENGTH = 10;
    const comment = item.comment.slice(0, MAX_COMMENT_LENGTH);
    return comment.length === item.comment.length ? comment : `${comment}...`;
  }, [item.comment]);

  const isAbsolute = useMemo(() => {
    return item.value_type === AlertValueType.ABSOLUTE;
  }, [item.value_type]);

  const iconName = useMemo((): IconType => {
    if (item.alert_type) return `alert-${item.alert_type}-${item.comparison_type}`;

    return "alert-stable-parameter";
  }, [item]);

  return (
    <div className="patient-alert-notification-row">
      <div className="alert-notification-row-value-wrapper">
        <div className="alert-notification-row-value-icon">
          <Icon type={iconName} />
        </div>
        <div className="alert-notification-row-value">
          <div className="alert-notification-row-value-top">
            {item.value_type !== AlertValueType.INSUFFICIENT_DATA ? (
              <span className="alert-notification-value">{notificationValue}</span>
            ) : null}

            <span className="alert-notification-signal-type">{notificationSignalType}</span>
            {differenceText ? <Icon className="alert-notification-percentage-icon" type={percentageIcon} /> : null}
            {differenceText ? (
              <span className={`alert-notification-percentage-value ${percentageColor}`}>
                {Number(differenceText).toFixed(0)}
                {isAbsolute ? "" : "%"}
              </span>
            ) : null}
          </div>
          <div className="alert-notification-row-value-bottom">{item.value_type}</div>
          {item.comment && tooltipCommentLabal ? (
            <div className="alert-notification-row-value-bottom item-comment">
              <Tooltip label={tooltipCommentLabal} text={item.comment} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="alert-notification-row-date">
        <div>
          {getFormattedDateTime(
            patientTimeZone ? (convertDateToTimezone(item.created_at, patientTimeZone) as Date) : item.created_at,
          )}
        </div>
      </div>
      <div className="alert-notification-row-name">
        {!item.is_read && onClickMarkAsRead && (
          <div className="alert-notification-list-read" onClick={onClickMarkAsRead.bind(null, item)}>
            Read
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertNotificationsRowComponent;
